// ============================================================================
// SS3 PHYSICS — COMPLETE LEARNING MODULE (8 TOPICS)
// NERDC Scheme of Work — First Term only; no Second or Third Term in NERDC scheme
// ============================================================================

export const WAEC_PHYSICS_LEARN_SS3 = [
  {
    "topic": "Conversion of Energy",
    "topicCode": "SS3-PHY-01",
    "module": "Energy and Machines",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Energy conversion</span> is the changing of energy from one form to another. Energy is never created or destroyed — this is the <span class=\"learn-keyword\">principle of conservation of energy</span> — only transformed, and in every real conversion some energy is lost as heat.</div><h3 class=\"learn-subheading\">1. Principle of Conservation of Energy</h3><p class=\"learn-p\">The total energy in an isolated system remains constant. When energy changes form, the total amount before and after the change is the same, although useful energy output is always less than input because of losses such as friction and heat.</p><h3 class=\"learn-subheading\">2. Common Energy Conversions</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Device</th><th>Energy conversion</th></tr></thead><tbody><tr><td>Electric iron / heater</td><td>Electrical → heat (thermal)</td></tr><tr><td>Generator / dynamo</td><td>Mechanical → electrical</td></tr><tr><td>Electric motor</td><td>Electrical → mechanical</td></tr><tr><td>Solar panel</td><td>Light (solar) → electrical</td></tr><tr><td>Battery (discharging)</td><td>Chemical → electrical</td></tr><tr><td>Battery (charging)</td><td>Electrical → chemical</td></tr><tr><td>Loudspeaker</td><td>Electrical → sound</td></tr><tr><td>Microphone</td><td>Sound → electrical</td></tr><tr><td>Bulb (filament)</td><td>Electrical → light and heat</td></tr><tr><td>Car engine</td><td>Chemical (fuel) → heat → mechanical</td></tr><tr><td>Photosynthesis</td><td>Light → chemical energy</td></tr><tr><td>Falling body</td><td>Potential → kinetic</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Efficiency of Energy Conversion</h3><p class=\"learn-p\">Efficiency = (useful energy output ÷ total energy input) × 100%. No machine is 100% efficient because some input energy is always converted to heat through friction or resistance.</p><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> When asked for the energy conversion in a device, state it as 'X energy to Y energy', in the correct order, e.g. 'electrical energy to mechanical energy' for a motor, not the reverse.</div>",
    "questions": [
      {
        "q": "Energy conversion is best defined as:",
        "o": [
          "the destruction of energy",
          "the changing of energy from one form to another",
          "the creation of new energy",
          "the storage of energy only"
        ],
        "a": 1,
        "e": "Energy conversion changes the form energy takes, without creating or destroying it.",
        "h": "Think of a change in form, not amount.",
        "yr": "SS3"
      },
      {
        "q": "The principle of conservation of energy states that energy:",
        "o": [
          "cannot be created or destroyed, only converted",
          "increases in every conversion",
          "is destroyed in every conversion",
          "is always created in a reaction"
        ],
        "a": 0,
        "e": "Total energy before and after a conversion stays the same.",
        "h": "Nothing is lost overall.",
        "yr": "SS3"
      },
      {
        "q": "An electric iron converts electrical energy to:",
        "o": [
          "heat energy",
          "sound energy",
          "chemical energy",
          "light energy"
        ],
        "a": 0,
        "e": "The heating element converts electrical energy to heat.",
        "h": "Think of what an iron does to clothes.",
        "yr": "SS3"
      },
      {
        "q": "A generator converts:",
        "o": [
          "mechanical energy to electrical energy",
          "chemical energy to heat energy",
          "electrical energy to mechanical energy",
          "light energy to electrical energy"
        ],
        "a": 0,
        "e": "A generator uses motion to produce electricity.",
        "h": "The opposite of a motor.",
        "yr": "SS3"
      },
      {
        "q": "An electric motor converts:",
        "o": [
          "mechanical energy to electrical energy",
          "electrical energy to mechanical energy",
          "heat energy to light energy",
          "chemical energy to electrical energy"
        ],
        "a": 1,
        "e": "A motor uses electricity to produce motion.",
        "h": "Think of a fan.",
        "yr": "SS3"
      },
      {
        "q": "A solar panel converts:",
        "o": [
          "electrical energy to light energy",
          "light energy to electrical energy",
          "sound energy to electrical energy",
          "heat energy to chemical energy"
        ],
        "a": 1,
        "e": "Solar cells convert sunlight directly to electricity.",
        "h": "Sunlight in, electricity out.",
        "yr": "SS3"
      },
      {
        "q": "A battery, while discharging, converts:",
        "o": [
          "mechanical energy to chemical energy",
          "heat energy to chemical energy",
          "electrical energy to chemical energy",
          "chemical energy to electrical energy"
        ],
        "a": 3,
        "e": "Stored chemical energy is released as electrical energy.",
        "h": "Think of use, not charging.",
        "yr": "SS3"
      },
      {
        "q": "While a battery is being charged, the conversion is:",
        "o": [
          "heat energy to electrical energy",
          "mechanical energy to electrical energy",
          "electrical energy to chemical energy",
          "chemical energy to electrical energy"
        ],
        "a": 2,
        "e": "Charging stores electrical energy as chemical energy.",
        "h": "Think of plugging it in.",
        "yr": "SS3"
      },
      {
        "q": "A loudspeaker converts:",
        "o": [
          "sound energy to electrical energy",
          "heat energy to sound energy",
          "mechanical energy to sound energy",
          "electrical energy to sound energy"
        ],
        "a": 3,
        "e": "Electrical signals are converted to sound.",
        "h": "Used for music playback.",
        "yr": "SS3"
      },
      {
        "q": "A microphone converts:",
        "o": [
          "sound energy to electrical energy",
          "light energy to sound energy",
          "electrical energy to sound energy",
          "heat energy to sound energy"
        ],
        "a": 0,
        "e": "It is the reverse of a loudspeaker.",
        "h": "Used for recording voice.",
        "yr": "SS3"
      },
      {
        "q": "A filament bulb converts electrical energy mainly to:",
        "o": [
          "only light energy",
          "light and heat energy",
          "chemical energy",
          "only heat energy"
        ],
        "a": 1,
        "e": "Filament bulbs are inefficient and waste much energy as heat.",
        "h": "Old-style bulbs get hot.",
        "yr": "SS3"
      },
      {
        "q": "A car engine converts chemical energy in fuel first to heat, then to:",
        "o": [
          "light energy",
          "sound energy only",
          "nuclear energy",
          "mechanical energy"
        ],
        "a": 3,
        "e": "Combustion releases heat which drives the pistons mechanically.",
        "h": "Think of what makes the car move.",
        "yr": "SS3"
      },
      {
        "q": "Photosynthesis converts:",
        "o": [
          "light energy to chemical energy",
          "heat energy to light energy",
          "mechanical energy to chemical energy",
          "chemical energy to light energy"
        ],
        "a": 0,
        "e": "Plants store solar energy as chemical energy in food.",
        "h": "Happens in green plants.",
        "yr": "SS3"
      },
      {
        "q": "A falling object converts potential energy to:",
        "o": [
          "kinetic energy",
          "electrical energy",
          "chemical energy",
          "sound energy only"
        ],
        "a": 0,
        "e": "As height decreases, speed increases.",
        "h": "Motion energy.",
        "yr": "SS3"
      },
      {
        "q": "The efficiency of a machine is calculated as:",
        "o": [
          "energy output minus energy input",
          "(useful energy output ÷ total energy input) × 100%",
          "total energy input ÷ useful output",
          "energy input × energy output"
        ],
        "a": 1,
        "e": "Efficiency compares useful output to total input.",
        "h": "A percentage of usefulness.",
        "yr": "SS3"
      },
      {
        "q": "No machine is 100% efficient mainly because of:",
        "o": [
          "too much light produced",
          "perfect conversion of energy",
          "excess electricity supply",
          "energy lost as heat through friction"
        ],
        "a": 3,
        "e": "Friction always wastes some energy as heat.",
        "h": "Think of resistance to motion.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following best shows the correct order for a device converting electrical energy to mechanical energy?",
        "o": [
          "Battery",
          "Generator",
          "Electric motor",
          "Solar panel"
        ],
        "a": 2,
        "e": "A motor takes in electricity and produces movement.",
        "h": "Used in fans and mixers.",
        "yr": "SS3"
      },
      {
        "q": "A dynamo is used mainly to convert:",
        "o": [
          "electrical energy to mechanical energy",
          "light energy to chemical energy",
          "chemical energy to mechanical energy",
          "mechanical energy to electrical energy"
        ],
        "a": 3,
        "e": "It works like a generator.",
        "h": "Found on old bicycles for lighting.",
        "yr": "SS3"
      },
      {
        "q": "Which device converts chemical energy to electrical energy directly?",
        "o": [
          "Battery (discharging)",
          "Loudspeaker",
          "Transformer",
          "Electric motor"
        ],
        "a": 0,
        "e": "A battery stores chemical energy and releases it as electricity.",
        "h": "Found in torches and phones.",
        "yr": "SS3"
      },
      {
        "q": "If a machine takes in 500 J of energy and produces 400 J of useful work, its efficiency is:",
        "o": [
          "50%",
          "100%",
          "80%",
          "125%"
        ],
        "a": 2,
        "e": "(400 ÷ 500) × 100 = 80%.",
        "h": "Divide output by input.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Transmission System",
    "topicCode": "SS3-PHY-02",
    "module": "Energy and Machines",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Transmission</span> is the process of transferring energy, motion or power from one point (the source) to another (where it is used). Transmission may be mechanical or electrical.</div><h3 class=\"learn-subheading\">1. Mechanical Transmission Systems</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>System</th><th>Description</th><th>Example use</th></tr></thead><tbody><tr><td>Gears</td><td>Toothed wheels that mesh together to transmit rotary motion and change speed, torque or direction</td><td>Car gearbox, clock mechanisms</td></tr><tr><td>Pulleys</td><td>Wheels with a groove carrying a belt or rope, used to transmit rotary motion over a distance or to lift loads</td><td>Engine fan belt, block and tackle</td></tr><tr><td>Levers</td><td>Rigid bars that turn about a fixed point (fulcrum) to transmit and multiply force</td><td>Crowbar, wheelbarrow, scissors</td></tr><tr><td>Belt drives</td><td>A belt connects two pulleys of different sizes to transmit rotary motion, often changing speed</td><td>Sewing machines, generators</td></tr><tr><td>Chain drives</td><td>A chain engages sprockets to transmit motion positively without slipping</td><td>Bicycles, motorcycles</td></tr></tbody></table></div><h3 class=\"learn-subheading\">2. Electrical Transmission Systems</h3><p class=\"learn-p\">Electrical power is transmitted from generating stations to consumers through <strong>power lines</strong> at very high voltage to reduce energy loss, then stepped down for use by <strong>transformers</strong>.</p><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Component</th><th>Function</th></tr></thead><tbody><tr><td>Step-up transformer</td><td>Increases voltage (and reduces current) for transmission at the power station</td></tr><tr><td>Transmission lines/cables</td><td>Carry high-voltage electricity over long distances</td></tr><tr><td>Step-down transformer</td><td>Reduces voltage to safe levels near consumers</td></tr><tr><td>Distribution network</td><td>Delivers electricity at usable voltage to homes and industries</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Importance of Efficient Transmission</h3><ul style=\"margin-left:20px;\"><li>Reduces power (I²R) losses as heat in the cables — high voltage means lower current for the same power, so less heat loss.</li><li>Ensures more energy reaches the consumer.</li><li>Reduces the cost of generation per unit delivered.</li><li>Prevents overheating and damage to cables and equipment.</li><li>Improves the reliability and safety of the power supply.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">🎯</span> <strong>Exam Tip:</strong> Power loss in a cable is I²R; transmitting at high voltage (low current) for the same power greatly reduces this loss.</div>",
    "questions": [
      {
        "q": "Transmission is best described as:",
        "o": [
          "destroying excess energy",
          "measuring energy accurately",
          "transferring energy, motion or power from one point to another",
          "storing energy permanently"
        ],
        "a": 2,
        "e": "It moves power from a source to where it is used.",
        "h": "Movement of power.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a mechanical transmission system?",
        "o": [
          "Power lines",
          "Step-down transformer",
          "Gears",
          "Transformers"
        ],
        "a": 2,
        "e": "Gears are mechanical, unlike transformers and power lines.",
        "h": "Toothed wheels.",
        "yr": "SS3"
      },
      {
        "q": "Gears transmit motion by:",
        "o": [
          "meshing toothed wheels together",
          "using electrical wires",
          "using magnetic fields only",
          "using belts only"
        ],
        "a": 0,
        "e": "The teeth interlock to transfer rotary motion.",
        "h": "Think of a clock.",
        "yr": "SS3"
      },
      {
        "q": "A belt drive transmits rotary motion using:",
        "o": [
          "a belt connecting two pulleys",
          "a chain and sprockets",
          "electric current",
          "toothed gears only"
        ],
        "a": 0,
        "e": "The belt links pulleys of possibly different sizes.",
        "h": "Found in sewing machines.",
        "yr": "SS3"
      },
      {
        "q": "A chain drive is commonly found in:",
        "o": [
          "solar panels",
          "transformers",
          "bicycles",
          "power lines"
        ],
        "a": 2,
        "e": "The chain engages the sprockets on the wheels and pedals.",
        "h": "Pedal-powered vehicle.",
        "yr": "SS3"
      },
      {
        "q": "A lever transmits and multiplies:",
        "o": [
          "frequency",
          "force",
          "current",
          "voltage"
        ],
        "a": 1,
        "e": "Levers turn about a fulcrum to multiply applied force.",
        "h": "Think of a crowbar.",
        "yr": "SS3"
      },
      {
        "q": "The main purpose of a step-up transformer at a power station is to:",
        "o": [
          "increase voltage for transmission",
          "decrease voltage for consumers",
          "convert electricity to heat",
          "store electrical energy"
        ],
        "a": 0,
        "e": "High voltage reduces current and power loss during transmission.",
        "h": "Raises voltage.",
        "yr": "SS3"
      },
      {
        "q": "A step-down transformer is mainly used to:",
        "o": [
          "increase voltage for transmission",
          "reduce voltage to safe levels for consumers",
          "generate electricity",
          "store electrical energy"
        ],
        "a": 1,
        "e": "It lowers voltage before delivery to homes.",
        "h": "Near consumers.",
        "yr": "SS3"
      },
      {
        "q": "Electrical power is transmitted at high voltage mainly to:",
        "o": [
          "reduce energy loss in the cables",
          "make the wires lighter",
          "increase resistance",
          "increase the current in the cables"
        ],
        "a": 0,
        "e": "Lower current at high voltage reduces I²R losses.",
        "h": "Think of heat loss in wires.",
        "yr": "SS3"
      },
      {
        "q": "The formula for power loss in a transmission cable is:",
        "o": [
          "VI²",
          "V/I",
          "IR²",
          "I²R"
        ],
        "a": 3,
        "e": "Power loss = current squared × resistance.",
        "h": "Current is squared.",
        "yr": "SS3"
      },
      {
        "q": "If current is halved while power delivered remains constant, power loss in the cable is reduced because:",
        "o": [
          "the cable becomes shorter",
          "power loss depends on the square of the current",
          "resistance increases",
          "voltage becomes zero"
        ],
        "a": 1,
        "e": "Since loss = I²R, halving current reduces loss to a quarter.",
        "h": "Squared relationship.",
        "yr": "SS3"
      },
      {
        "q": "A distribution network is responsible for:",
        "o": [
          "generating electricity from fuel",
          "transmitting electricity at very high voltage only",
          "storing surplus electricity",
          "delivering electricity at usable voltage to consumers"
        ],
        "a": 3,
        "e": "It is the final stage before use.",
        "h": "Reaches homes and factories.",
        "yr": "SS3"
      },
      {
        "q": "Which transmission system uses a rope or belt in a groove?",
        "o": [
          "Transformer",
          "Pulley",
          "Gear",
          "Chain drive"
        ],
        "a": 1,
        "e": "A pulley has a grooved wheel.",
        "h": "Used to lift loads.",
        "yr": "SS3"
      },
      {
        "q": "A wheelbarrow works on the principle of a:",
        "o": [
          "gear",
          "transformer",
          "lever",
          "pulley"
        ],
        "a": 2,
        "e": "It has a fulcrum at the wheel.",
        "h": "Think of moving load with a single wheel.",
        "yr": "SS3"
      },
      {
        "q": "Efficient electrical transmission reduces:",
        "o": [
          "cost of energy loss and equipment damage",
          "the amount of electricity generated",
          "the price of fuel",
          "the number of consumers"
        ],
        "a": 0,
        "e": "Less loss means more delivered power for the same generation.",
        "h": "Saves money and equipment.",
        "yr": "SS3"
      },
      {
        "q": "Which system changes the speed or direction of rotary motion using toothed wheels?",
        "o": [
          "Transformer",
          "Belt drive",
          "Power lines",
          "Gears"
        ],
        "a": 3,
        "e": "The teeth of gears interlock for precise transmission.",
        "h": "Precise motion transfer.",
        "yr": "SS3"
      },
      {
        "q": "Power lines that carry electricity over long distances operate at:",
        "o": [
          "very high voltage",
          "very low voltage",
          "constant low current only",
          "zero voltage"
        ],
        "a": 0,
        "e": "High voltage minimises transmission losses.",
        "h": "Pylons carry it.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is an electrical transmission component?",
        "o": [
          "Chain drive",
          "Lever",
          "Gear",
          "Transformer"
        ],
        "a": 3,
        "e": "Transformers are part of the electrical transmission system.",
        "h": "Changes voltage.",
        "yr": "SS3"
      },
      {
        "q": "A bicycle's pedal motion is transmitted to the rear wheel mainly by a:",
        "o": [
          "gear train alone",
          "transformer",
          "hydraulic system",
          "chain drive"
        ],
        "a": 3,
        "e": "The chain links the pedal sprocket to the rear wheel sprocket.",
        "h": "Metal loop of links.",
        "yr": "SS3"
      },
      {
        "q": "The overall importance of efficient transmission systems is to:",
        "o": [
          "maximise the energy delivered to the point of use",
          "eliminate the need for power stations",
          "reduce the number of generators needed only",
          "increase friction in machines"
        ],
        "a": 0,
        "e": "Efficient systems minimise losses between source and user.",
        "h": "Get more useful energy through.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Uses of Machines",
    "topicCode": "SS3-PHY-03",
    "module": "Energy and Machines",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">A <span class=\"learn-keyword\">machine</span> is any device that makes work easier by changing the size, direction or speed of an applied force.</div><h3 class=\"learn-subheading\">1. Key Definitions</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Term</th><th>Meaning</th><th>Formula</th></tr></thead><tbody><tr><td>Load</td><td>The resistance or weight to be overcome</td><td>—</td></tr><tr><td>Effort</td><td>The force applied to the machine</td><td>—</td></tr><tr><td>Mechanical Advantage (MA)</td><td>Ratio of load to effort</td><td>MA = Load ÷ Effort</td></tr><tr><td>Velocity Ratio (VR)</td><td>Ratio of distance moved by effort to distance moved by load</td><td>VR = distance moved by effort ÷ distance moved by load</td></tr><tr><td>Efficiency</td><td>How much of the input work is turned into useful output work</td><td>Efficiency = (MA ÷ VR) × 100%</td></tr></tbody></table></div><h3 class=\"learn-subheading\">2. Types of Simple Machines</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Machine</th><th>Example</th><th>Everyday use</th></tr></thead><tbody><tr><td>Lever</td><td>Crowbar, see-saw, wheelbarrow</td><td>Lifting or moving heavy loads with less effort</td></tr><tr><td>Pulley</td><td>Single and combined pulley systems</td><td>Lifting loads such as a bucket from a well, cranes</td></tr><tr><td>Wheel and axle</td><td>Doorknob, steering wheel, windlass</td><td>Turning a small force into a larger turning effect</td></tr><tr><td>Inclined plane</td><td>Ramp, staircase</td><td>Raising loads gradually with less force</td></tr><tr><td>Screw</td><td>Bolt, jack, drill bit</td><td>Fastening and lifting with mechanical advantage</td></tr><tr><td>Wedge</td><td>Axe, knife, chisel</td><td>Splitting or cutting with concentrated force</td></tr><tr><td>Hydraulic press</td><td>Car jack, hydraulic brakes</td><td>Multiplying force using Pascal's principle in a fluid</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Applications of Machines in Everyday Life</h3><ul style=\"margin-left:20px;\"><li>Cranes use pulley and lever systems to lift heavy building materials.</li><li>Hydraulic presses use Pascal's principle (pressure is transmitted equally in a fluid) to lift cars in workshops.</li><li>The wheel and axle in a steering wheel lets a small force turn the front wheels of a car.</li><li>Levers in scissors and pliers multiply the cutting or gripping force.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Efficiency is always less than 100% because of friction; MA is usually less than VR in a real machine.</div>",
    "questions": [
      {
        "q": "A machine is best defined as a device that:",
        "o": [
          "destroys applied force",
          "makes work easier by changing the size, direction or speed of a force",
          "creates energy from nothing",
          "increases friction only"
        ],
        "a": 1,
        "e": "Machines make tasks easier without creating energy.",
        "h": "It aids work.",
        "yr": "SS3"
      },
      {
        "q": "Mechanical Advantage (MA) is defined as:",
        "o": [
          "Load ÷ Effort",
          "Effort ÷ Load",
          "Effort − Load",
          "Load × Effort"
        ],
        "a": 0,
        "e": "MA compares the load lifted to the effort applied.",
        "h": "Load on top.",
        "yr": "SS3"
      },
      {
        "q": "Velocity Ratio (VR) is defined as:",
        "o": [
          "effort ÷ load",
          "distance moved by effort ÷ distance moved by load",
          "load ÷ effort",
          "distance moved by load ÷ distance moved by effort"
        ],
        "a": 1,
        "e": "VR compares the distances moved by effort and load.",
        "h": "Effort's distance on top.",
        "yr": "SS3"
      },
      {
        "q": "Efficiency of a machine can be calculated as:",
        "o": [
          "(MA ÷ VR) × 100%",
          "(VR ÷ MA) × 100%",
          "MA × VR",
          "MA + VR"
        ],
        "a": 0,
        "e": "It compares mechanical advantage to velocity ratio.",
        "h": "MA over VR.",
        "yr": "SS3"
      },
      {
        "q": "A crowbar is an example of a:",
        "o": [
          "inclined plane",
          "lever",
          "wheel and axle",
          "pulley"
        ],
        "a": 1,
        "e": "It turns about a fixed pivot point.",
        "h": "Used to pry objects.",
        "yr": "SS3"
      },
      {
        "q": "A see-saw works on the principle of a:",
        "o": [
          "hydraulic press",
          "lever",
          "screw",
          "wedge"
        ],
        "a": 1,
        "e": "It balances about a central fulcrum.",
        "h": "Playground equipment.",
        "yr": "SS3"
      },
      {
        "q": "Which machine is used to lift a bucket from a well?",
        "o": [
          "Inclined plane",
          "Wedge",
          "Screw",
          "Pulley"
        ],
        "a": 3,
        "e": "A pulley changes the direction of the applied force.",
        "h": "Rope over a wheel.",
        "yr": "SS3"
      },
      {
        "q": "A doorknob is an example of a:",
        "o": [
          "wheel and axle",
          "pulley",
          "lever",
          "wedge"
        ],
        "a": 0,
        "e": "Turning the larger knob rotates the smaller shaft with greater force.",
        "h": "Turns to open a door.",
        "yr": "SS3"
      },
      {
        "q": "A ramp used to roll heavy loads up to a platform is an example of a(n):",
        "o": [
          "wedge",
          "screw",
          "inclined plane",
          "wheel and axle"
        ],
        "a": 2,
        "e": "It reduces the force needed by increasing the distance.",
        "h": "A sloped surface.",
        "yr": "SS3"
      },
      {
        "q": "A bolt is an example of a:",
        "o": [
          "screw",
          "pulley",
          "wedge",
          "lever"
        ],
        "a": 0,
        "e": "It has a helical thread.",
        "h": "Fastening device.",
        "yr": "SS3"
      },
      {
        "q": "A knife or axe blade works as a:",
        "o": [
          "wheel and axle",
          "lever",
          "screw",
          "wedge"
        ],
        "a": 3,
        "e": "It concentrates force into a thin edge to split material.",
        "h": "Sharp cutting tool.",
        "yr": "SS3"
      },
      {
        "q": "A hydraulic press works based on:",
        "o": [
          "Newton's third law only",
          "Ohm's law",
          "Pascal's principle",
          "the law of conservation of mass only"
        ],
        "a": 2,
        "e": "Pressure applied to an enclosed fluid is transmitted equally in all directions.",
        "h": "Fluid pressure principle.",
        "yr": "SS3"
      },
      {
        "q": "Cranes commonly use which combination of simple machines?",
        "o": [
          "Screw and wedge only",
          "Inclined plane only",
          "Wheel and axle only",
          "Pulley and lever systems"
        ],
        "a": 3,
        "e": "Cranes combine several simple machines to lift heavy loads.",
        "h": "Lifting heavy building materials.",
        "yr": "SS3"
      },
      {
        "q": "In a real machine, efficiency is always:",
        "o": [
          "less than 100%",
          "zero",
          "exactly 100%",
          "more than 100%"
        ],
        "a": 0,
        "e": "Friction and other losses prevent perfect efficiency.",
        "h": "Some energy is always lost.",
        "yr": "SS3"
      },
      {
        "q": "If MA = 4 and VR = 5, the efficiency of the machine is:",
        "o": [
          "80%",
          "100%",
          "125%",
          "20%"
        ],
        "a": 0,
        "e": "(4 ÷ 5) × 100 = 80%.",
        "h": "MA divided by VR.",
        "yr": "SS3"
      },
      {
        "q": "A wheelbarrow used to carry load is an example of a:",
        "o": [
          "wheel and axle",
          "screw",
          "wedge",
          "lever"
        ],
        "a": 3,
        "e": "The wheel acts as the fulcrum.",
        "h": "One-wheeled cart.",
        "yr": "SS3"
      },
      {
        "q": "Scissors work using the combined action of:",
        "o": [
          "two pulleys",
          "two levers",
          "two screws",
          "two wedges"
        ],
        "a": 1,
        "e": "Each blade acts as a lever about the pivot.",
        "h": "Cutting tool with two blades.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best describes the 'load' in a machine?",
        "o": [
          "The force applied by the user",
          "The resistance or weight to be overcome",
          "The efficiency of the machine",
          "The speed of the machine"
        ],
        "a": 1,
        "e": "Load is what the machine works against.",
        "h": "What is being lifted or moved.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best describes 'effort' in a machine?",
        "o": [
          "The resistance to be overcome",
          "The efficiency percentage",
          "The distance moved by the load",
          "The force applied to operate the machine"
        ],
        "a": 3,
        "e": "Effort is the input force.",
        "h": "What the user applies.",
        "yr": "SS3"
      },
      {
        "q": "A jack used to lift a car for tyre changing works mainly as a:",
        "o": [
          "pulley only",
          "transformer",
          "chain drive",
          "screw or hydraulic mechanism"
        ],
        "a": 3,
        "e": "Car jacks commonly use a screw thread or hydraulic fluid.",
        "h": "Lifts vehicles for repair.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Repair and Maintenance of Machines",
    "topicCode": "SS3-PHY-04",
    "module": "Energy and Machines",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Repair</span> means fixing a machine that has broken down or malfunctioned, while <span class=\"learn-keyword\">maintenance</span> means the routine care given to a machine to keep it working well and prevent breakdowns.</div><h3 class=\"learn-subheading\">1. Common Faults in Machines</h3><ul style=\"margin-left:20px;\"><li>Wear and tear of moving parts due to friction.</li><li>Lack of lubrication causing overheating and excessive wear.</li><li>Corrosion or rusting of metal parts, especially in humid conditions.</li><li>Loose or broken components (bolts, belts, gears).</li><li>Electrical faults such as short circuits, worn insulation or blown fuses.</li><li>Blockage or accumulation of dirt and dust in moving parts.</li></ul><h3 class=\"learn-subheading\">2. Types of Maintenance</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Type</th><th>Meaning</th><th>Example</th></tr></thead><tbody><tr><td>Preventive (routine) maintenance</td><td>Regular scheduled checks and servicing carried out before a fault occurs, to prevent breakdown</td><td>Oiling a machine regularly, changing engine oil every few months, cleaning filters</td></tr><tr><td>Corrective (breakdown) maintenance</td><td>Repair carried out after a machine has already broken down or failed</td><td>Replacing a burnt-out motor, fixing a punctured tyre</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Importance of Lubrication, Cleaning and Routine Checks</h3><ul style=\"margin-left:20px;\"><li><strong>Lubrication</strong> reduces friction between moving parts, reducing wear and heat generation, and improving efficiency.</li><li><strong>Cleaning</strong> removes dust and debris that could cause blockage, corrosion or increased friction.</li><li><strong>Routine checks</strong> detect faults early, before they cause major breakdowns, saving cost and downtime.</li></ul><h3 class=\"learn-subheading\">4. Benefits of Good Maintenance</h3><ul style=\"margin-left:20px;\"><li>Extends the working life of machines.</li><li>Reduces the cost of major repairs and replacement.</li><li>Improves safety for operators.</li><li>Keeps machines working at maximum efficiency.</li><li>Reduces unplanned downtime in industry.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">🎯</span> <strong>Exam Tip:</strong> Preventive maintenance is carried out BEFORE a fault occurs; corrective maintenance is carried out AFTER a fault has occurred.</div>",
    "questions": [
      {
        "q": "Maintenance is best described as:",
        "o": [
          "routine care given to a machine to keep it working well",
          "destroying a faulty machine",
          "fixing a machine only after it breaks down",
          "ignoring a machine's condition"
        ],
        "a": 0,
        "e": "Maintenance prevents faults from developing.",
        "h": "Ongoing care.",
        "yr": "SS3"
      },
      {
        "q": "Repair is best described as:",
        "o": [
          "buying a brand-new machine",
          "routine servicing before a fault occurs",
          "fixing a machine that has already broken down",
          "cleaning a machine only"
        ],
        "a": 2,
        "e": "Repair addresses an existing fault.",
        "h": "After the fault.",
        "yr": "SS3"
      },
      {
        "q": "Preventive maintenance is carried out:",
        "o": [
          "only once in a machine's lifetime",
          "after a fault occurs",
          "never, as it is unnecessary",
          "before a fault occurs"
        ],
        "a": 3,
        "e": "It is scheduled maintenance to prevent breakdown.",
        "h": "Proactive.",
        "yr": "SS3"
      },
      {
        "q": "Corrective maintenance is carried out:",
        "o": [
          "at random with no reason",
          "before any fault develops",
          "only during manufacturing",
          "after a fault or breakdown has occurred"
        ],
        "a": 3,
        "e": "It responds to an existing problem.",
        "h": "Reactive.",
        "yr": "SS3"
      },
      {
        "q": "Changing engine oil regularly is an example of:",
        "o": [
          "corrective maintenance",
          "preventive maintenance",
          "a common fault",
          "a type of transmission"
        ],
        "a": 1,
        "e": "It is done routinely to avoid engine damage.",
        "h": "Done on a schedule.",
        "yr": "SS3"
      },
      {
        "q": "Replacing a burnt-out motor after failure is an example of:",
        "o": [
          "routine cleaning",
          "lubrication",
          "corrective maintenance",
          "preventive maintenance"
        ],
        "a": 2,
        "e": "This repair happens after the fault occurs.",
        "h": "After failure.",
        "yr": "SS3"
      },
      {
        "q": "Lubrication mainly helps to:",
        "o": [
          "add unnecessary weight",
          "increase friction for better grip",
          "reduce friction between moving parts",
          "increase corrosion"
        ],
        "a": 2,
        "e": "Oil or grease reduces resistance between surfaces.",
        "h": "Reduces resistance.",
        "yr": "SS3"
      },
      {
        "q": "Lack of lubrication in a machine can lead to:",
        "o": [
          "overheating and excessive wear",
          "longer machine life",
          "reduced friction",
          "improved efficiency"
        ],
        "a": 0,
        "e": "Without lubrication, parts rub and wear faster.",
        "h": "The opposite of the benefit of oiling.",
        "yr": "SS3"
      },
      {
        "q": "Corrosion of metal parts is most common in:",
        "o": [
          "outer space",
          "dry, cool conditions",
          "vacuum conditions",
          "humid conditions"
        ],
        "a": 3,
        "e": "Moisture speeds up rusting.",
        "h": "Water and air cause rust.",
        "yr": "SS3"
      },
      {
        "q": "Cleaning a machine regularly helps to prevent:",
        "o": [
          "corrosion of plastic parts only",
          "all electrical faults instantly",
          "permanent breakdown with no cause",
          "blockage and increased friction from dirt and debris"
        ],
        "a": 3,
        "e": "Dirt build-up can hinder moving parts.",
        "h": "Removes debris.",
        "yr": "SS3"
      },
      {
        "q": "Routine checks on machines help to:",
        "o": [
          "detect faults early before they cause major breakdown",
          "eliminate the need for lubrication",
          "increase the cost of repairs",
          "guarantee a machine never breaks down"
        ],
        "a": 0,
        "e": "Early detection saves cost and downtime.",
        "h": "Catch problems early.",
        "yr": "SS3"
      },
      {
        "q": "A loose or broken bolt in a machine is an example of a:",
        "o": [
          "an energy conversion",
          "a benefit of maintenance",
          "a type of maintenance",
          "common fault"
        ],
        "a": 3,
        "e": "Loose components are a typical mechanical fault.",
        "h": "A mechanical problem.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is an electrical fault in a machine?",
        "o": [
          "A worn gear tooth",
          "A loose belt",
          "A rusted bolt",
          "A blown fuse"
        ],
        "a": 3,
        "e": "Fuses and wiring issues are electrical, not mechanical, faults.",
        "h": "Related to current flow.",
        "yr": "SS3"
      },
      {
        "q": "Good maintenance practices help to:",
        "o": [
          "extend the working life of machines",
          "shorten the life of machines",
          "increase breakdown frequency",
          "reduce machine safety"
        ],
        "a": 0,
        "e": "Regular care keeps machines functional longer.",
        "h": "Machines last longer.",
        "yr": "SS3"
      },
      {
        "q": "Unplanned downtime in industry is best reduced by:",
        "o": [
          "ignoring machine faults",
          "using machines without servicing",
          "corrective maintenance alone",
          "preventive maintenance"
        ],
        "a": 3,
        "e": "Preventing faults avoids unexpected stoppages.",
        "h": "Avoids surprises.",
        "yr": "SS3"
      },
      {
        "q": "Which best differentiates preventive from corrective maintenance?",
        "o": [
          "Both are done only after faults occur",
          "Both are done only before faults occur",
          "There is no difference between them",
          "Preventive is scheduled before faults; corrective is done after faults occur"
        ],
        "a": 3,
        "e": "Timing relative to the fault is the key difference.",
        "h": "Before vs after.",
        "yr": "SS3"
      },
      {
        "q": "Improving safety for machine operators is a benefit of:",
        "o": [
          "avoiding lubrication",
          "ignoring routine checks",
          "good maintenance",
          "allowing corrosion to develop"
        ],
        "a": 2,
        "e": "Well-maintained machines are less likely to injure operators.",
        "h": "Safety benefit.",
        "yr": "SS3"
      },
      {
        "q": "A common cause of wear and tear in machines is:",
        "o": [
          "routine cleaning",
          "friction between moving parts",
          "excess lubrication",
          "preventive maintenance"
        ],
        "a": 1,
        "e": "Friction gradually wears down surfaces.",
        "h": "Rubbing parts.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following reduces the cost of major repairs?",
        "o": [
          "Ignoring faults until breakdown",
          "Regular preventive maintenance",
          "Removing lubrication",
          "Avoiding cleaning"
        ],
        "a": 1,
        "e": "Catching problems early avoids expensive repairs later.",
        "h": "Prevention saves money.",
        "yr": "SS3"
      },
      {
        "q": "The main goal of maintenance, whether preventive or corrective, is to:",
        "o": [
          "keep machines working efficiently and safely",
          "increase the number of faults",
          "make machines less safe",
          "reduce a machine's lifespan"
        ],
        "a": 0,
        "e": "Both types of maintenance aim for reliable operation.",
        "h": "Reliable operation.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Dams and Energy Production",
    "topicCode": "SS3-PHY-05",
    "module": "Energy and Machines",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">A <span class=\"learn-keyword\">dam</span> is a large barrier built across a river to hold back water, creating a reservoir. Dams are used for irrigation, flood control, water supply and generating <span class=\"learn-keyword\">hydroelectric power</span>.</div><h3 class=\"learn-subheading\">1. Types of Dams</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Type</th><th>Main purpose</th></tr></thead><tbody><tr><td>Hydroelectric dam</td><td>Generates electricity by using the force of falling or flowing water to turn turbines</td></tr><tr><td>Irrigation dam</td><td>Stores water for supplying farmland during the dry season</td></tr><tr><td>Multipurpose dam</td><td>Combines uses — electricity generation, irrigation, flood control and water supply</td></tr></tbody></table></div><h3 class=\"learn-subheading\">2. How Hydroelectric Power is Generated</h3><ul style=\"margin-left:20px;\"><li>Water is stored behind the dam wall in a reservoir at a height.</li><li>The stored water is released through a controlled channel (penstock) and falls under gravity, gaining kinetic energy.</li><li>The moving water turns the blades of a turbine.</li><li>The turbine is connected to a generator, which converts the mechanical (rotational) energy into electrical energy.</li><li>The electricity produced is transmitted through power lines to consumers.</li></ul><h3 class=\"learn-subheading\">3. Examples of Dams in Nigeria</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Dam</th><th>River / State</th><th>Main use</th></tr></thead><tbody><tr><td>Kainji Dam</td><td>River Niger, Niger State</td><td>Hydroelectric power generation</td></tr><tr><td>Shiroro Dam</td><td>River Kaduna, Niger State</td><td>Hydroelectric power generation</td></tr><tr><td>Jebba Dam</td><td>River Niger, Kwara State</td><td>Hydroelectric power generation</td></tr></tbody></table></div><h3 class=\"learn-subheading\">4. Advantages and Disadvantages of Dams</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Advantages</th><th>Disadvantages</th></tr></thead><tbody><tr><td>Renewable and clean source of electricity</td><td>High cost of construction</td></tr><tr><td>Provides water for irrigation and domestic use</td><td>Displacement of communities from flooded areas</td></tr><tr><td>Controls flooding downstream</td><td>Disruption of aquatic ecosystems and fish migration</td></tr><tr><td>Supports fishing and recreation on the reservoir</td><td>Silting of the reservoir reduces its capacity over time</td></tr><tr><td>Long-lasting once built, with low running cost</td><td>Risk of dam failure causing catastrophic flooding</td></tr></tbody></table></div><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> In hydroelectric generation, the energy conversion sequence is: potential energy of stored water → kinetic energy of falling water → mechanical energy of the turbine → electrical energy from the generator.</div>",
    "questions": [
      {
        "q": "A dam is best described as:",
        "o": [
          "a type of power line",
          "a type of turbine",
          "a large barrier built across a river to hold back water",
          "a small channel for irrigation only"
        ],
        "a": 2,
        "e": "It creates a reservoir behind it.",
        "h": "Barrier across a river.",
        "yr": "SS3"
      },
      {
        "q": "A dam built mainly to generate electricity is called a:",
        "o": [
          "hydroelectric dam",
          "irrigation dam",
          "flood-only dam",
          "navigation dam"
        ],
        "a": 0,
        "e": "It uses falling water to turn turbines.",
        "h": "Produces power.",
        "yr": "SS3"
      },
      {
        "q": "A dam built mainly to store water for farmland is called a(n):",
        "o": [
          "hydroelectric dam",
          "flood barrier only",
          "irrigation dam",
          "multipurpose dam only"
        ],
        "a": 2,
        "e": "It supplies water during dry periods.",
        "h": "For farming.",
        "yr": "SS3"
      },
      {
        "q": "A dam that combines electricity generation, irrigation and flood control is called a:",
        "o": [
          "hydroelectric dam only",
          "navigation dam only",
          "irrigation dam only",
          "multipurpose dam"
        ],
        "a": 3,
        "e": "It serves more than one function.",
        "h": "Several uses combined.",
        "yr": "SS3"
      },
      {
        "q": "In hydroelectric power generation, stored water has mainly:",
        "o": [
          "nuclear energy",
          "kinetic energy only",
          "chemical energy",
          "potential energy"
        ],
        "a": 3,
        "e": "Water at height has stored gravitational potential energy.",
        "h": "Energy due to height.",
        "yr": "SS3"
      },
      {
        "q": "As stored water falls through the penstock, its potential energy converts to:",
        "o": [
          "nuclear energy",
          "kinetic energy",
          "sound energy only",
          "chemical energy"
        ],
        "a": 1,
        "e": "Falling water gains speed and kinetic energy.",
        "h": "Energy of motion.",
        "yr": "SS3"
      },
      {
        "q": "In a hydroelectric power station, falling water directly turns the:",
        "o": [
          "turbine",
          "battery",
          "transformer",
          "capacitor"
        ],
        "a": 0,
        "e": "The moving water spins the turbine blades.",
        "h": "Turns blades.",
        "yr": "SS3"
      },
      {
        "q": "The turbine in a hydroelectric station is connected to a:",
        "o": [
          "battery only",
          "solar panel",
          "transformer only",
          "generator"
        ],
        "a": 3,
        "e": "The generator converts mechanical energy to electrical energy.",
        "h": "Produces electricity.",
        "yr": "SS3"
      },
      {
        "q": "Which Nigerian dam is located on the River Niger in Niger State?",
        "o": [
          "Tiga Dam",
          "Challawa Gorge Dam",
          "Shiroro Dam",
          "Kainji Dam"
        ],
        "a": 3,
        "e": "Kainji Dam is a major hydroelectric dam on the Niger.",
        "h": "Major Niger River dam.",
        "yr": "SS3"
      },
      {
        "q": "Shiroro Dam is located on the River:",
        "o": [
          "Cross",
          "Niger",
          "Benue",
          "Kaduna"
        ],
        "a": 3,
        "e": "It is in Niger State on the Kaduna River.",
        "h": "Named similar to a state but on a different river.",
        "yr": "SS3"
      },
      {
        "q": "Jebba Dam is located in:",
        "o": [
          "Kano State",
          "Lagos State",
          "Kwara State",
          "Niger State only"
        ],
        "a": 2,
        "e": "It is on the River Niger in Kwara State.",
        "h": "South of Kainji.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is an advantage of dams?",
        "o": [
          "They never control flooding",
          "They provide a renewable source of electricity",
          "They destroy all aquatic life permanently",
          "They always reduce local water supply"
        ],
        "a": 1,
        "e": "Hydropower is renewable and clean.",
        "h": "A positive of dam building.",
        "yr": "SS3"
      },
      {
        "q": "A disadvantage of building large dams is:",
        "o": [
          "unlimited free electricity",
          "lower construction cost",
          "improved fish migration always",
          "displacement of communities from flooded areas"
        ],
        "a": 3,
        "e": "Reservoirs can flood inhabited land.",
        "h": "People must relocate.",
        "yr": "SS3"
      },
      {
        "q": "Silting of a dam's reservoir over time leads to:",
        "o": [
          "reduced water storage capacity",
          "increased electricity output",
          "improved fish migration",
          "lower construction cost"
        ],
        "a": 0,
        "e": "Sediment build-up reduces the reservoir's volume.",
        "h": "Sediment accumulation.",
        "yr": "SS3"
      },
      {
        "q": "Dams can disrupt:",
        "o": [
          "only soil fertility",
          "aquatic ecosystems and fish migration",
          "only noise levels",
          "only air quality"
        ],
        "a": 1,
        "e": "Blocking a river changes the habitat for aquatic life.",
        "h": "River life.",
        "yr": "SS3"
      },
      {
        "q": "The correct energy conversion sequence in hydroelectric generation is:",
        "o": [
          "chemical → electrical → kinetic → potential",
          "potential → kinetic → mechanical → electrical",
          "kinetic → chemical → electrical → potential",
          "electrical → mechanical → kinetic → potential"
        ],
        "a": 1,
        "e": "Stored water's potential energy ends as electricity.",
        "h": "Follow the order of the process.",
        "yr": "SS3"
      },
      {
        "q": "One major disadvantage of dam construction is:",
        "o": [
          "instant construction time",
          "unlimited lifespan with no risk",
          "high cost of construction",
          "zero maintenance cost"
        ],
        "a": 2,
        "e": "Dams require large capital investment.",
        "h": "Expensive to build.",
        "yr": "SS3"
      },
      {
        "q": "A benefit of dams for local communities near the reservoir is:",
        "o": [
          "free unlimited water without management",
          "zero environmental impact",
          "support for fishing and recreation",
          "guaranteed absence of flooding risk"
        ],
        "a": 2,
        "e": "Reservoirs can support fishing activities.",
        "h": "Positive use of the reservoir.",
        "yr": "SS3"
      },
      {
        "q": "Risk of catastrophic flooding downstream is associated with:",
        "o": [
          "proper dam maintenance",
          "irrigation channels only",
          "dam failure",
          "solar panel installation"
        ],
        "a": 2,
        "e": "If a dam bursts, it can flood areas downstream suddenly.",
        "h": "A dam's structural failure.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is NOT a type of dam mentioned in the scheme?",
        "o": [
          "Tidal dam",
          "Multipurpose dam",
          "Hydroelectric dam",
          "Irrigation dam"
        ],
        "a": 0,
        "e": "Tidal dams are not among the three types listed.",
        "h": "Not one of the three main types.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Rockets and Satellites",
    "topicCode": "SS3-PHY-06",
    "module": "Space Science and Technology",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">A <span class=\"learn-keyword\">rocket</span> is a vehicle that propels itself by expelling exhaust gas at high speed, working by <span class=\"learn-keyword\">Newton's third law of motion</span>: for every action there is an equal and opposite reaction.</div><h3 class=\"learn-subheading\">1. Principle of Rocket Propulsion</h3><p class=\"learn-p\">A rocket burns fuel to produce hot gases, which are forced out of the rear at high speed (the action). By Newton's third law, this pushes the rocket forward in the opposite direction with equal force (the reaction). Rockets can work in the vacuum of space because they carry their own oxidiser and do not need air to push against.</p><h3 class=\"learn-subheading\">2. Types of Rockets</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Type</th><th>Description</th></tr></thead><tbody><tr><td>Solid-fuel rockets</td><td>Use solid propellant mixed with an oxidiser; simple, reliable, but cannot be easily stopped once ignited</td></tr><tr><td>Liquid-fuel rockets</td><td>Use liquid fuel and liquid oxidiser stored in separate tanks and pumped into a combustion chamber; can be throttled, stopped and restarted, giving more control</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Types of Satellites</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Type</th><th>Function</th></tr></thead><tbody><tr><td>Communication satellites</td><td>Relay television, telephone and internet signals over long distances</td></tr><tr><td>Navigation satellites</td><td>Provide positioning data, e.g. the Global Positioning System (GPS)</td></tr><tr><td>Weather satellites</td><td>Monitor cloud patterns, storms and climate for forecasting</td></tr><tr><td>Military/reconnaissance satellites</td><td>Used for surveillance and national security</td></tr><tr><td>Earth observation/remote-sensing satellites</td><td>Monitor land use, agriculture, disaster and environmental change</td></tr></tbody></table></div><h3 class=\"learn-subheading\">4. Importance of Satellites in Modern Technology</h3><ul style=\"margin-left:20px;\"><li>Enable global telecommunications, television broadcasting and internet access.</li><li>Provide accurate weather forecasting and disaster early-warning systems.</li><li>Support navigation for aviation, shipping and personal devices (GPS).</li><li>Allow monitoring of the environment, agriculture and natural resources.</li><li>Support scientific research and national security.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">🎯</span> <strong>Exam Tip:</strong> Rocket propulsion is explained by Newton's third law, not Newton's first or second law — remember 'action and reaction are equal and opposite'.</div>",
    "questions": [
      {
        "q": "Rocket propulsion works based on:",
        "o": [
          "the law of conservation of mass only",
          "Newton's third law of motion",
          "Newton's first law of motion",
          "Newton's second law of motion only"
        ],
        "a": 1,
        "e": "For every action there is an equal and opposite reaction.",
        "h": "Action and reaction.",
        "yr": "SS3"
      },
      {
        "q": "The 'action' in rocket propulsion is:",
        "o": [
          "the rocket staying still",
          "hot gases expelled from the rear at high speed",
          "the rocket losing weight only",
          "the fuel remaining unused"
        ],
        "a": 1,
        "e": "Exhaust gases are forced backward.",
        "h": "Gas expelled.",
        "yr": "SS3"
      },
      {
        "q": "The 'reaction' in rocket propulsion causes the rocket to:",
        "o": [
          "slow down immediately",
          "move forward in the opposite direction",
          "stay stationary",
          "move sideways only"
        ],
        "a": 1,
        "e": "The equal and opposite force pushes the rocket forward.",
        "h": "Opposite direction to gas.",
        "yr": "SS3"
      },
      {
        "q": "Rockets can work in the vacuum of space because they:",
        "o": [
          "rely entirely on Earth's gravity",
          "push against the air behind them",
          "carry their own oxidiser and do not need air",
          "use only solar power to move"
        ],
        "a": 2,
        "e": "Unlike jet engines, rockets do not need atmospheric oxygen.",
        "h": "No air needed.",
        "yr": "SS3"
      },
      {
        "q": "Solid-fuel rockets use:",
        "o": [
          "electrical batteries alone",
          "solid propellant mixed with an oxidiser",
          "only compressed air",
          "liquid fuel and liquid oxidiser in separate tanks"
        ],
        "a": 1,
        "e": "The solid mixture burns to produce thrust.",
        "h": "Fixed, pre-mixed fuel.",
        "yr": "SS3"
      },
      {
        "q": "A disadvantage of solid-fuel rockets is that they:",
        "o": [
          "require no oxidiser",
          "use two separate fuel tanks",
          "cannot easily be stopped once ignited",
          "can be throttled and restarted easily"
        ],
        "a": 2,
        "e": "Once lit, solid fuel burns until exhausted.",
        "h": "No easy shutdown.",
        "yr": "SS3"
      },
      {
        "q": "Liquid-fuel rockets store fuel and oxidiser:",
        "o": [
          "mixed together permanently",
          "in separate tanks",
          "without any oxidiser",
          "only as a gas"
        ],
        "a": 1,
        "e": "They are pumped together in a combustion chamber.",
        "h": "Kept apart until use.",
        "yr": "SS3"
      },
      {
        "q": "An advantage of liquid-fuel rockets is that they can be:",
        "o": [
          "used without any oxidiser",
          "launched without fuel",
          "only ignited once",
          "throttled, stopped and restarted"
        ],
        "a": 3,
        "e": "This gives more control over thrust.",
        "h": "More controllable.",
        "yr": "SS3"
      },
      {
        "q": "Which type of satellite relays television and internet signals?",
        "o": [
          "Weather satellite",
          "Military satellite",
          "Communication satellite",
          "Navigation satellite"
        ],
        "a": 2,
        "e": "It carries communication signals over long distances.",
        "h": "Broadcasting and internet.",
        "yr": "SS3"
      },
      {
        "q": "Which type of satellite provides positioning data such as GPS?",
        "o": [
          "Weather satellite",
          "Communication satellite",
          "Navigation satellite",
          "Earth observation satellite"
        ],
        "a": 2,
        "e": "GPS relies on navigation satellites.",
        "h": "Used for location.",
        "yr": "SS3"
      },
      {
        "q": "Which type of satellite monitors cloud patterns and storms?",
        "o": [
          "Military satellite",
          "Navigation satellite",
          "Weather satellite",
          "Communication satellite"
        ],
        "a": 2,
        "e": "It helps with forecasting.",
        "h": "Used for forecasting.",
        "yr": "SS3"
      },
      {
        "q": "Military or reconnaissance satellites are mainly used for:",
        "o": [
          "weather forecasting only",
          "internet access only",
          "television broadcasting only",
          "surveillance and national security"
        ],
        "a": 3,
        "e": "They support defence and security operations.",
        "h": "Security purposes.",
        "yr": "SS3"
      },
      {
        "q": "Earth observation satellites are used to monitor:",
        "o": [
          "only military bases",
          "only underground minerals",
          "land use, agriculture and environmental change",
          "only ocean depth"
        ],
        "a": 2,
        "e": "They provide remote-sensing data for planning.",
        "h": "Monitoring the land.",
        "yr": "SS3"
      },
      {
        "q": "Satellites support aviation and shipping mainly through:",
        "o": [
          "reducing aircraft weight",
          "navigation services",
          "increasing fuel efficiency directly",
          "weather-proofing aircraft"
        ],
        "a": 1,
        "e": "Navigation satellites help determine position.",
        "h": "Helps with direction.",
        "yr": "SS3"
      },
      {
        "q": "A key importance of satellites in disaster management is:",
        "o": [
          "causing disasters",
          "increasing fuel cost",
          "preventing all communication",
          "providing early-warning systems"
        ],
        "a": 3,
        "e": "They help detect and warn of hazards.",
        "h": "Warning systems.",
        "yr": "SS3"
      },
      {
        "q": "Which law explains why a rocket moves forward when gas is expelled backward?",
        "o": [
          "The law of gravity only",
          "Boyle's law",
          "Newton's third law of motion",
          "Ohm's law"
        ],
        "a": 2,
        "e": "Action and reaction are equal and opposite.",
        "h": "Third of Newton's laws.",
        "yr": "SS3"
      },
      {
        "q": "A key difference between solid and liquid rocket fuel is that liquid fuel:",
        "o": [
          "allows more control over the thrust",
          "cannot be used for satellites",
          "cannot be stored in tanks",
          "burns only in space"
        ],
        "a": 0,
        "e": "Liquid systems can be throttled and restarted.",
        "h": "Controllability.",
        "yr": "SS3"
      },
      {
        "q": "Communication satellites are important because they enable:",
        "o": [
          "the end of television broadcasting",
          "only local radio in one city",
          "global telecommunications and broadcasting",
          "complete elimination of internet use"
        ],
        "a": 2,
        "e": "They connect distant regions for communication.",
        "h": "Long-distance signals.",
        "yr": "SS3"
      },
      {
        "q": "The importance of satellites in modern technology includes support for:",
        "o": [
          "elimination of all rockets",
          "destruction of weather systems",
          "scientific research and national security",
          "only agriculture in one country"
        ],
        "a": 2,
        "e": "Satellites serve many sectors, including research and defence.",
        "h": "Wide-ranging uses.",
        "yr": "SS3"
      },
      {
        "q": "What propels a rocket upward as its fuel burns and gas is expelled downward?",
        "o": [
          "The reaction force from expelled gas",
          "Magnetic repulsion from the ground",
          "Air resistance pushing it up",
          "Gravity pulling it upward"
        ],
        "a": 0,
        "e": "This is Newton's third law in action.",
        "h": "Equal and opposite force.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Niger-SAT 1",
    "topicCode": "SS3-PHY-07",
    "module": "Space Science and Technology",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">NigeriaSat-1</span> was Nigeria's first satellite, launched in <span class=\"learn-keyword\">2003</span>, marking the beginning of Nigeria's involvement in space technology.</div><h3 class=\"learn-subheading\">1. Background</h3><p class=\"learn-p\">NigeriaSat-1 was launched on 27 September 2003 from Plesetsk, Russia, aboard a Kosmos-3M rocket. It was built with the support of Surrey Satellite Technology Limited (SSTL) in the United Kingdom, under Nigeria's National Space Research and Development Agency (NASRDA), and formed part of the Disaster Monitoring Constellation (DMC), a group of small satellites from different countries working together.</p><h3 class=\"learn-subheading\">2. Objectives of NigeriaSat-1</h3><ul style=\"margin-left:20px;\"><li><strong>Environmental monitoring:</strong> tracking desertification, deforestation, erosion and other environmental changes.</li><li><strong>Disaster management:</strong> monitoring and providing early warning for floods, oil spills, drought and other disasters.</li><li><strong>Agricultural planning:</strong> assessing crop conditions, land use and food security.</li><li><strong>Security:</strong> monitoring of borders and national resources.</li></ul><h3 class=\"learn-subheading\">3. Achievements of NigeriaSat-1</h3><ul style=\"margin-left:20px;\"><li>Provided satellite imagery used in disaster response, including monitoring of the 2005 Hurricane Katrina and the Asian tsunami relief efforts.</li><li>Supported mapping and environmental monitoring within Nigeria.</li><li>Positioned Nigeria as the first sub-Saharan African country to own and operate its own satellite.</li><li>Built local capacity and expertise in satellite technology through NASRDA.</li></ul><h3 class=\"learn-subheading\">4. Challenges of NigeriaSat-1</h3><ul style=\"margin-left:20px;\"><li>The satellite eventually failed in orbit in 2012 due to a technical malfunction.</li><li>Limited local technical capacity meant heavy reliance on foreign expertise (SSTL) for design and construction.</li><li>High cost of satellite technology and launch.</li><li>Limited public awareness and use of the data it provided.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Do not confuse NigeriaSat-1 (2003, earth observation/remote sensing) with NigComSat-1 (2007, communications).</div>",
    "questions": [
      {
        "q": "NigeriaSat-1 was launched in:",
        "o": [
          "2003",
          "1999",
          "2011",
          "2007"
        ],
        "a": 0,
        "e": "It was Nigeria's first satellite, launched in September 2003.",
        "h": "Nigeria's first satellite year.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 was launched aboard a rocket from:",
        "o": [
          "Kourou, French Guiana",
          "Xichang, China",
          "Plesetsk, Russia",
          "Cape Canaveral, USA"
        ],
        "a": 2,
        "e": "It launched on a Kosmos-3M rocket from Russia.",
        "h": "A Russian launch site.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 was built with the support of:",
        "o": [
          "NASA",
          "the European Space Agency alone",
          "China Great Wall Industry Corporation",
          "Surrey Satellite Technology Limited (SSTL)"
        ],
        "a": 3,
        "e": "SSTL in the UK helped build it.",
        "h": "A British satellite company.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 was operated under Nigeria's:",
        "o": [
          "Federal Ministry of Health",
          "Nigerian National Petroleum Corporation",
          "Central Bank of Nigeria",
          "National Space Research and Development Agency (NASRDA)"
        ],
        "a": 3,
        "e": "NASRDA oversees Nigeria's space programme.",
        "h": "The national space agency.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 was part of a group of satellites known as the:",
        "o": [
          "Global Positioning System (GPS)",
          "International Space Station (ISS)",
          "African Union Satellite Network",
          "Disaster Monitoring Constellation (DMC)"
        ],
        "a": 3,
        "e": "The DMC combined satellites from different countries.",
        "h": "A multi-country satellite group.",
        "yr": "SS3"
      },
      {
        "q": "One objective of NigeriaSat-1 was:",
        "o": [
          "deep-sea exploration",
          "nuclear weapons testing",
          "manned space travel",
          "environmental monitoring"
        ],
        "a": 3,
        "e": "It tracked desertification, deforestation and erosion.",
        "h": "Watching the environment.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 supported disaster management by:",
        "o": [
          "monitoring floods, oil spills and drought",
          "stopping volcanic eruptions",
          "preventing earthquakes directly",
          "controlling ocean currents"
        ],
        "a": 0,
        "e": "It provided imagery for disaster response.",
        "h": "Early warning support.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 assisted agricultural planning by:",
        "o": [
          "assessing crop conditions and land use",
          "training farmers directly",
          "building irrigation dams",
          "manufacturing fertiliser"
        ],
        "a": 0,
        "e": "Remote sensing data supported food security planning.",
        "h": "Crop monitoring from space.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 supported national security by:",
        "o": [
          "monitoring borders and resources",
          "producing ammunition",
          "building military weapons",
          "training soldiers"
        ],
        "a": 0,
        "e": "It provided surveillance-type imagery.",
        "h": "Border and resource monitoring.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 provided imagery used in the response to:",
        "o": [
          "World War II",
          "Hurricane Katrina and the Asian tsunami",
          "the 2008 global financial crisis",
          "the Nigerian Civil War"
        ],
        "a": 1,
        "e": "Its data supported international disaster relief efforts.",
        "h": "Major natural disasters in the 2000s.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 positioned Nigeria as:",
        "o": [
          "the first Asian country with a satellite",
          "the last African country to attempt space technology",
          "the first sub-Saharan African country to own and operate its own satellite",
          "the first country in the world to launch a satellite"
        ],
        "a": 2,
        "e": "This was a major achievement for Nigeria.",
        "h": "A regional first.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 helped build local capacity in:",
        "o": [
          "textile manufacturing",
          "agricultural mechanisation only",
          "satellite technology through NASRDA",
          "oil refining"
        ],
        "a": 2,
        "e": "It contributed to Nigerian expertise in space science.",
        "h": "Technical skill-building.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 eventually failed in orbit in:",
        "o": [
          "2003",
          "1999",
          "2012",
          "2007"
        ],
        "a": 2,
        "e": "A technical malfunction ended its operation.",
        "h": "About nine years after launch.",
        "yr": "SS3"
      },
      {
        "q": "A major challenge of NigeriaSat-1 was:",
        "o": [
          "heavy reliance on foreign expertise for design and construction",
          "too many Nigerian engineers involved",
          "excess funding from the government",
          "no international partnerships at all"
        ],
        "a": 0,
        "e": "SSTL's involvement shows the reliance on foreign support.",
        "h": "Dependence on outside help.",
        "yr": "SS3"
      },
      {
        "q": "Another challenge of NigeriaSat-1 was:",
        "o": [
          "it being extremely cheap to build",
          "zero technical requirements",
          "the high cost of satellite technology and launch",
          "no need for international partners"
        ],
        "a": 2,
        "e": "Satellite programmes are capital-intensive.",
        "h": "Expensive technology.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1's main focus, unlike NigComSat-1, was on:",
        "o": [
          "telecommunications and broadcasting",
          "earth observation and remote sensing",
          "military weapons development",
          "deep space exploration"
        ],
        "a": 1,
        "e": "NigeriaSat-1 focused on imagery, not communications.",
        "h": "Observing the Earth's surface.",
        "yr": "SS3"
      },
      {
        "q": "Which agency launched and managed NigeriaSat-1 on Nigeria's behalf?",
        "o": [
          "NASRDA",
          "NAFDAC",
          "NNPC",
          "NCC"
        ],
        "a": 0,
        "e": "The National Space Research and Development Agency.",
        "h": "Space-focused agency.",
        "yr": "SS3"
      },
      {
        "q": "The Disaster Monitoring Constellation (DMC) that included NigeriaSat-1 was made up of satellites from:",
        "o": [
          "only Nigeria",
          "only European countries",
          "only the United States",
          "different countries working together"
        ],
        "a": 3,
        "e": "It was an international cooperative effort.",
        "h": "A multinational group.",
        "yr": "SS3"
      },
      {
        "q": "Limited public awareness of NigeriaSat-1's data was a:",
        "o": [
          "achievement of the programme",
          "challenge of the programme",
          "type of rocket fuel",
          "objective of the programme"
        ],
        "a": 1,
        "e": "Underuse of available data limited its impact.",
        "h": "A shortfall, not a success.",
        "yr": "SS3"
      },
      {
        "q": "NigeriaSat-1 is best classified as a(n):",
        "o": [
          "weather satellite only",
          "earth observation / remote-sensing satellite",
          "communication satellite",
          "navigation satellite only"
        ],
        "a": 1,
        "e": "Its main role was imaging the Earth's surface.",
        "h": "Observes land from space.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "NICOM-SAT 1",
    "topicCode": "SS3-PHY-08",
    "module": "Space Science and Technology",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">NigComSat-1</span> was Nigeria's first communications satellite, launched in <span class=\"learn-keyword\">2007</span>, to boost telecommunications and broadcasting capacity.</div><h3 class=\"learn-subheading\">1. Launch of NigComSat-1</h3><p class=\"learn-p\">NigComSat-1 was launched on 13 May 2007 from Xichang, China, aboard a Chinese Long March rocket, built by the China Great Wall Industry Corporation for Nigeria's National Space Research and Development Agency (NASRDA). It was Africa's first high-powered communications satellite.</p><h3 class=\"learn-subheading\">2. Uses of NigComSat-1</h3><ul style=\"margin-left:20px;\"><li>Provided <strong>communication services</strong> such as telephone and broadcasting signal relay.</li><li>Expanded access to <strong>internet services</strong>, especially in remote and rural areas.</li><li>Supported <strong>television and radio broadcasting</strong> across Nigeria and neighbouring countries.</li><li>Supported e-learning, e-governance, and other applications requiring wide-area connectivity.</li></ul><h3 class=\"learn-subheading\">3. Challenges</h3><ul style=\"margin-left:20px;\"><li>NigComSat-1 failed in orbit in November 2008 due to a solar power anomaly, only about 18 months after launch.</li><li>A replacement, <strong>NigComSat-1R</strong>, was launched in December 2011.</li><li>High dependence on foreign technology and launch providers.</li><li>High cost of satellite replacement and insurance.</li><li>Limited technical capacity for satellite control locally.</li></ul><h3 class=\"learn-subheading\">4. Impact on Nigeria's Development</h3><ul style=\"margin-left:20px;\"><li>Demonstrated Nigeria's ambition to use space technology for national development.</li><li>Improved the case for expanding telecommunications infrastructure, especially in underserved areas.</li><li>Encouraged further investment in Nigeria's space programme through NASRDA.</li><li>Provided lessons that shaped the design and management of NigComSat-1R.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">🎯</span> <strong>Exam Tip:</strong> NigComSat-1 (2007) is a communications satellite; it failed in 2008 and was replaced by NigComSat-1R in 2011.</div>",
    "questions": [
      {
        "q": "NigComSat-1 was launched in:",
        "o": [
          "2007",
          "2003",
          "2012",
          "1999"
        ],
        "a": 0,
        "e": "It was Nigeria's first communications satellite.",
        "h": "Four years after NigeriaSat-1.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 was launched from:",
        "o": [
          "Kourou, French Guiana",
          "Plesetsk, Russia",
          "Cape Canaveral, USA",
          "Xichang, China"
        ],
        "a": 3,
        "e": "It launched aboard a Chinese Long March rocket.",
        "h": "A Chinese launch site.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 was built by:",
        "o": [
          "China Great Wall Industry Corporation",
          "Surrey Satellite Technology Limited",
          "the European Space Agency",
          "NASA"
        ],
        "a": 0,
        "e": "A Chinese company built it for NASRDA.",
        "h": "A Chinese aerospace company.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 was described as:",
        "o": [
          "the world's first satellite ever launched",
          "a navigation satellite only",
          "a weather satellite only",
          "Africa's first high-powered communications satellite"
        ],
        "a": 3,
        "e": "It was a major milestone for African telecommunications.",
        "h": "First of its kind in Africa.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 was managed under Nigeria's:",
        "o": [
          "Federal Ministry of Works",
          "Nigerian Communications Commission only",
          "National Space Research and Development Agency (NASRDA)",
          "Central Bank of Nigeria"
        ],
        "a": 2,
        "e": "NASRDA oversees Nigeria's satellites.",
        "h": "The national space agency.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 mainly provided:",
        "o": [
          "crop monitoring only",
          "communication services such as telephone and broadcasting relay",
          "border surveillance only",
          "weather forecasting only"
        ],
        "a": 1,
        "e": "Its focus was telecommunications, unlike NigeriaSat-1.",
        "h": "Focused on communication.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 helped expand access to:",
        "o": [
          "ocean mapping only",
          "mineral exploration only",
          "internet services, especially in remote areas",
          "space tourism"
        ],
        "a": 2,
        "e": "It improved connectivity where infrastructure was limited.",
        "h": "Internet access.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 supported which broadcasting services?",
        "o": [
          "Only postal services",
          "Television and radio broadcasting",
          "Only telegraph services",
          "Only newspaper printing"
        ],
        "a": 1,
        "e": "It relayed broadcast signals across Nigeria and neighbours.",
        "h": "Radio and TV.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 supported applications requiring wide-area connectivity such as:",
        "o": [
          "manual farming only",
          "oil drilling only",
          "e-learning and e-governance",
          "mineral mining only"
        ],
        "a": 2,
        "e": "It enabled remote digital services.",
        "h": "Online education and government services.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 failed in orbit in:",
        "o": [
          "2012",
          "2015",
          "2003",
          "November 2008"
        ],
        "a": 3,
        "e": "It failed about 18 months after launch.",
        "h": "Just over a year after launch.",
        "yr": "SS3"
      },
      {
        "q": "The cause of NigComSat-1's failure was:",
        "o": [
          "a cyberattack",
          "a launch explosion",
          "a collision with another satellite",
          "a solar power anomaly"
        ],
        "a": 3,
        "e": "A problem with its solar power system caused the failure.",
        "h": "Power system issue.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 was replaced by:",
        "o": [
          "NigeriaSat-2 only",
          "NigComSat-2 only",
          "NASRDA-Sat",
          "NigComSat-1R"
        ],
        "a": 3,
        "e": "The replacement launched in December 2011.",
        "h": "Same name with an 'R'.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1R was launched in:",
        "o": [
          "December 2011",
          "2007",
          "2003",
          "2008"
        ],
        "a": 0,
        "e": "It replaced the failed NigComSat-1.",
        "h": "A few years after the failure.",
        "yr": "SS3"
      },
      {
        "q": "A challenge of NigComSat-1 was:",
        "o": [
          "complete independence from all foreign support",
          "zero cost of replacement",
          "unlimited satellite lifespan",
          "high dependence on foreign technology and launch providers"
        ],
        "a": 3,
        "e": "Nigeria relied on China for design and launch.",
        "h": "Reliance on outside partners.",
        "yr": "SS3"
      },
      {
        "q": "Another challenge of NigComSat-1 was:",
        "o": [
          "guaranteed satellite lifespan",
          "extremely low replacement cost",
          "the high cost of satellite replacement and insurance",
          "no need for any insurance"
        ],
        "a": 2,
        "e": "Replacing a failed satellite is expensive.",
        "h": "Costly to replace.",
        "yr": "SS3"
      },
      {
        "q": "A further challenge was limited:",
        "o": [
          "technical capacity for satellite control locally",
          "funding from the government initially",
          "access to electricity in Nigeria",
          "interest from NASRDA"
        ],
        "a": 0,
        "e": "Nigeria depended on external expertise for control.",
        "h": "Local skills gap.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 demonstrated Nigeria's ambition to:",
        "o": [
          "use space technology for national development",
          "avoid international partnerships",
          "focus only on agriculture",
          "abandon all space programmes"
        ],
        "a": 0,
        "e": "It was part of a broader development vision.",
        "h": "A development goal.",
        "yr": "SS3"
      },
      {
        "q": "The experience with NigComSat-1 helped shape:",
        "o": [
          "the design and management of NigComSat-1R",
          "the construction of Kainji Dam",
          "Nigeria's oil policy",
          "Nigeria's football programme"
        ],
        "a": 0,
        "e": "Lessons learned informed the replacement satellite.",
        "h": "Improved the next satellite.",
        "yr": "SS3"
      },
      {
        "q": "Unlike NigeriaSat-1, NigComSat-1's main purpose was:",
        "o": [
          "communications rather than earth observation",
          "earth observation rather than communications",
          "weather forecasting only",
          "navigation only"
        ],
        "a": 0,
        "e": "NigComSat-1 focused on relaying signals, not imaging.",
        "h": "Compare its purpose to NigeriaSat-1.",
        "yr": "SS3"
      },
      {
        "q": "NigComSat-1 is best classified as a(n):",
        "o": [
          "weather satellite only",
          "communication satellite",
          "navigation satellite only",
          "earth observation satellite"
        ],
        "a": 1,
        "e": "Its core function was communications relay.",
        "h": "Focused on signal relay.",
        "yr": "SS3"
      }
    ]
  }
];
